import React, { useLayoutEffect, useRef, useState } from "react";

import "./AuditItem.css";
import { useTranslation } from 'react-i18next';
import { IAudit } from "src/app/audit/~store/models/Audit";
import { ConvertFromAuditStatus } from "src/app/audit/helpers/audit-helper"
import { getDateTime } from "src/app/shared/helpers/date-helper/DateFormats";
import { AuditType } from "src/app/audit/~store/models/enums/AuditTypes";

interface IAuditItemProps {
    audit: IAudit,
    operatorName: string,
    onClick: (audit: IAudit) => void
}

const getBadgeClass = (type: AuditType): string => {
    switch (type) {
        case AuditType.OperatorConnectedBySignalR:
        case AuditType.OperatorWentOnline:
        case AuditType.CreateSetting:
        case AuditType.CreateChannel:
        case AuditType.CreateOperator:
            return "success";
        case AuditType.OperatorDisconnectedBySignalR:
        case AuditType.OperatorWentOffline:
        case AuditType.RemoveSetting:
        case AuditType.RemoveChannel:
        case AuditType.RemoveOperator:
            return "danger";
        case AuditType.OperatorWentPause:
        case AuditType.UpdateSetting:
        case AuditType.UpdateChannel:
        case AuditType.UpdateOperator:
        case AuditType.OperatorWentPause1:
        case AuditType.OperatorWentPause2:
        case AuditType.OperatorWentPause3:
        case AuditType.OperatorWentPause4:
        case AuditType.OperatorWentPause5:
        case AuditType.OperatorWentPause6:
        case AuditType.OperatorWentPause7:
        case AuditType.OperatorWentPause8:
        case AuditType.OperatorWentPause9:
        case AuditType.OperatorWentPause10:
            return "warning";
        default:
            return "";
    }
};

const tryParse = (val: any) => {
    if (typeof val !== 'string') return val;
    try {
        const parsed = JSON.parse(val);
        return (parsed && typeof parsed === 'object') ? parsed : val;
    } catch {
        return val;
    }
};

const DataRenderer = ({ data }: { data: any }) => {
    const currentData = tryParse(data);

    if (!currentData || typeof currentData !== 'object') {
        return <span title={String(currentData)}>{String(currentData)}</span>;
    }

    if ('From' in currentData || 'To' in currentData) {
        return (
            <div className="audit-diff-container">
                {['From', 'To'].map(key => currentData[key] && (
                    <div key={key} className={`audit-diff-block ${key.toLowerCase()}`}>
                        <span className="audit-diff-label">{key}:</span>
                        <DataRenderer data={currentData[key]} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="audit-kv-list">
            {Object.entries(currentData).map(([key, val]) => (
                <div key={key} className="audit-kv-row">
                    <span className="audit-key">{key}:</span>
                    <span className="audit-value">
                        <DataRenderer data={val} />
                    </span>
                </div>
            ))}
        </div>
    );
};

export const AuditItem = (props: IAuditItemProps) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (contentRef.current) {
            const hasOverflow = contentRef.current.scrollHeight > contentRef.current.clientHeight;
            setIsOverflowing(hasOverflow);
        }

    }, [props.audit.data]);; 

    const _onSelectAuditHandler = () => {
        let selection = window?.getSelection()?.toString();

        if (!selection || selection.length <= 0) {
            props.onClick(props.audit)
        }
    }

    const toggleExpand = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); 
        setIsExpanded(!isExpanded);
    };

    const badgeType = getBadgeClass(props.audit.type);

    return (
        <tr className="audit-item" onClick={() => _onSelectAuditHandler()}>
            <td className={`col-badge-indicator ${badgeType}`}></td>
            <td className="col-id">
                <div className="audit-tags-container">
                    <div className="audit-id-tag tag">
                        #{props.audit.auditId}
                    </div>
                </div>
            </td>
            <td className="col-date">{getDateTime(props.audit.timestamp)}</td>
            <td className="col-operator">{props.operatorName}</td>
            <td className="col-type">  {ConvertFromAuditStatus(props.audit.type)}
            </td>
            <td className="col-data">
                <div ref={contentRef} className={`audit-data-wrapper ${isExpanded ? 'opened' : ''}`}>
                    <DataRenderer data={props.audit.data} />
                </div>
                {isOverflowing && (
                    <div className="audit-expand-button-hitbox" onClick={toggleExpand}>
                        <div className={`audit-expand-button ${isExpanded ? 'opened' : ''}`} />
                    </div>
                )}
            </td>
        </tr>
    );
}